import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import CRMTable from '@/components/Table/CRM/CRMTable'
import CRMBoard from '@/components/Table/CRM/CRMBoard'
import CrmToast from '@/components/Table/CRM/CrmToast'
import NewTaskButton from '@/components/Table/CRM/NewTaskButton'
import TaskCreator from './consumer/CRM/TaskCreator'
import PageHeaderWithIcons from '@/components/heder/PageHeaderWithIcons'
import FilterArea from './CRM/FilterArea'
import { useCRMStore } from '@/store/crm/crmStore'
import { useAppTheme } from '@/theme/ThemeContext'
import { useCrmAccountId } from '@/hooks/useCrm'
import type { CrmFilterResource } from '@/services/crmService'
import { CRM_SECTIONS } from '@/utils/crm'
import { useSearch } from '@/hooks/useSearch'

const CRM = () => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const accountId = useCrmAccountId()
  const { crmTaskCreatorModal, setCRMTaskCreatorModal } = useCRMStore()
  const [filters, setFilters] = useState<CrmFilterResource[]>([])
  const [view, setView] = useState<'list' | 'board'>('list')
  // The tables fetch through React Query, which stops an old request when its key changes. The
  // hook gives the shared pause and the text; the tables show work while the text waits.
  const search = useSearch()
  const waitingForPause = search.text.trim() !== search.query

  const tableFilters = useMemo<CrmFilterResource[]>(
    () =>
      search.query
        ? [
            ...filters,
            { name: 'search', value: [search.query], operator: 'CONTAINS' },
          ]
        : filters,
    [filters, search.query]
  )

  const handleClose = () => {
    setCRMTaskCreatorModal({ mode: null, open: false, data: null })
  }

  const handleOpen = () => {
    setCRMTaskCreatorModal({ open: true, mode: 'create', data: null })
  }

  const BG_COLOR = isDarkMode ? '#091327' : '#F8FAFD'

  return (
    <Box
      sx={{
        backgroundColor: BG_COLOR,
        padding: '49px',
        boxSizing: 'border-box',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <PageHeaderWithIcons
        padding={false}
        title="CRM"
        showSearch
        placeholder="חיפוש טקסט חופשי..."
        onSearchChange={search.setText}
        executeSearch={search.searchNow}
      />
      <Box mt="36px">
        {accountId ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <NewTaskButton onClick={handleOpen} />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={view}
                  onChange={(_, next) => next && setView(next)}
                  aria-label="תצוגת משימות"
                  sx={{ fontFamily: 'Rubik, sans-serif' }}
                >
                  <ToggleButton value="list">רשימה</ToggleButton>
                  <ToggleButton value="board">לוח</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <FilterArea accountId={accountId} onSearch={setFilters} />
            </Box>

            {view === 'board' ? (
              <CRMBoard
                accountId={accountId}
                filters={tableFilters}
                searching={waitingForPause}
              />
            ) : (
              CRM_SECTIONS.map(section => (
                <CRMTable
                  key={section}
                  type={section}
                  accountId={accountId}
                  filters={tableFilters}
                  searching={waitingForPause}
                />
              ))
            )}
            <TaskCreator
              open={crmTaskCreatorModal.open}
              handleClose={handleClose}
            />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Rubik", sans-serif',
                fontSize: 18,
                fontWeight: 300,
                color: isDarkMode ? '#9CA3AF' : '#6B7280',
              }}
            >
              אין נתונים להצגה
            </Typography>
          </Box>
        )}
      </Box>
      <CrmToast />
    </Box>
  )
}

export default CRM
